// Eltern-Portal: RPC-Verfügbarkeit cachen, fehlende Funktionen nicht wiederholt aufrufen.

import { getActiveTenantId } from "../platform/active-tenant.js";
import { registerHubCacheClear } from "../hub/hub-scoped-cache.js";

function __asStr(x) {
  return String(x ?? "").trim();
}

const __missingRpcs = new Set();
const __failedRpcs = new Map();
const __inFlightRpc = new Map();
/** @type {Map<string, { ts: number, value: { ok: boolean, data?: unknown, reason?: string, error?: unknown } }>} */
const __successRpc = new Map();

const __RPC_FAIL_CACHE_MS = 120_000;
const __RPC_SUCCESS_CACHE_MS = 20_000;

export function clearParentPortalRpcSuccessCache() {
  __successRpc.clear();
  __inFlightRpc.clear();
}

registerHubCacheClear(clearParentPortalRpcSuccessCache);

function __rpcScopePrefix() {
  return `tenant:${__asStr(getActiveTenantId())}`;
}

function __isRpcFailureCached(rpcName) {
  const name = __asStr(rpcName);
  const failedAt = __failedRpcs.get(name);
  if (!failedAt) return false;
  if (Date.now() - failedAt > __RPC_FAIL_CACHE_MS) {
    __failedRpcs.delete(name);
    return false;
  }
  return true;
}

function __markRpcFailed(rpcName) {
  const name = __asStr(rpcName);
  if (name) __failedRpcs.set(name, Date.now());
}

export function isParentPortalRpcMissingError(error) {
  const code = __asStr(error?.code);
  const msg = __asStr(error?.message).toLowerCase();
  const status = Number(error?.status ?? error?.statusCode);
  return (
    code === "PGRST202" ||
    code === "42883" ||
    status === 404 ||
    msg.includes("could not find the function") ||
    (msg.includes("function") && msg.includes("does not exist"))
  );
}

export function isParentPortalRpcTransportError(error) {
  const status = Number(error?.status ?? error?.statusCode);
  return status === 405 || status === 502 || status === 503;
}

export function isParentPortalRpcKnownMissing(rpcName) {
  return __missingRpcs.has(__asStr(rpcName));
}

function __markRpcMissing(rpcName) {
  const name = __asStr(rpcName);
  if (name) __missingRpcs.add(name);
}

/**
 * @returns {Promise<{ ok: boolean, data?: unknown, reason?: string, error?: unknown }>}
 */
export async function callParentPortalRpcCached(
  supabase,
  rpcName,
  args = undefined,
) {
  const name = __asStr(rpcName);
  if (!supabase || !name) return { ok: false, reason: "no-supabase" };
  if (__missingRpcs.has(name)) return { ok: false, reason: "rpc-missing" };
  if (__isRpcFailureCached(name))
    return { ok: false, reason: "rpc-cached-error" };

  const flightKey =
    args === undefined
      ? `${__rpcScopePrefix()}|${name}`
      : `${__rpcScopePrefix()}|${name}:${JSON.stringify(args)}`;
  const cached = __successRpc.get(flightKey);
  if (cached && Date.now() - cached.ts < __RPC_SUCCESS_CACHE_MS) {
    return cached.value;
  }
  if (__inFlightRpc.has(flightKey)) {
    return __inFlightRpc.get(flightKey);
  }

  const promise = (async () => {
    try {
      const { data, error } =
        args === undefined
          ? await supabase.rpc(name)
          : await supabase.rpc(name, args);
      if (error) {
        if (isParentPortalRpcMissingError(error)) {
          __markRpcMissing(name);
          return { ok: false, reason: "rpc-missing", error };
        }
        if (isParentPortalRpcTransportError(error)) {
          __markRpcFailed(name);
        }
        return { ok: false, reason: "rpc-error", error };
      }
      __failedRpcs.delete(name);
      const okValue = { ok: true, data };
      // Nur erfolgreiche Leseergebnisse cachen – Fehler nie als Erfolg.
      __successRpc.set(flightKey, { ts: Date.now(), value: okValue });
      return okValue;
    } catch (e) {
      __markRpcFailed(name);
      return { ok: false, reason: "exception", error: e };
    } finally {
      __inFlightRpc.delete(flightKey);
    }
  })();

  __inFlightRpc.set(flightKey, promise);
  return promise;
}

/**
 * @returns {Promise<string>}
 */
export async function ensureParentPortalViewerPersonRemote(supabase) {
  const res = await callParentPortalRpcCached(
    supabase,
    "trainerstunden_parent_portal_ensure_viewer_person",
  );
  if (!res.ok) return "";
  return __asStr(res.data);
}
